import { usePagination } from "../../shared/lib/use-pagination.js";
import { Pagination } from "../../shared/ui/pagination.js";
import { Link, useSearchParams } from "react-router-dom";

import { useBots } from "../bots/queries.js";
import { useAccounts } from "../settings/queries.js";
import { Button } from "../../shared/ui/button.js";
import { RefreshButton } from "../../shared/ui/refresh-button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { activityTypes } from "./models.js";
import { formatUsd } from "../positions/models.js";
import { useActivities, useActivitySummary } from "./queries.js";

export function ActivitiesPage() {
  const [search, setSearch] = useSearchParams();
  const cursor = search.get("cursor") ?? "";
  const bots = useBots({});
  const accounts = useAccounts();
  const accountFilter = search.get("account") ?? "";
  const activityFilter = search.get("activity") ?? "";
  const botFilter = search.get("bot") ?? "";
  const correlationFilter = search.get("correlation") ?? "";
  const fromFilter = search.get("from") ?? "";
  const fillFilter = search.get("fill") ?? "";
  const orderFilter = search.get("order") ?? "";
  const runFilter = search.get("run") ?? "";
  const statusFilter = search.get("status") ?? "";
  const symbolFilter = search.get("symbol") ?? "";
  const toFilter = search.get("to") ?? "";
  const typeFilter = search.get("type") ?? "";
  const activities = useActivities(cursor, {
    ...(accountFilter === "" ? {} : { brokerAccountId: accountFilter }),
    ...(botFilter === "" ? {} : { botId: botFilter }),
    ...(runFilter === "" ? {} : { botRunId: runFilter }),
    ...(orderFilter === "" ? {} : { orderId: orderFilter }),
    ...(fillFilter === "" ? {} : { fillId: fillFilter }),
    ...(symbolFilter === "" ? {} : { symbol: symbolFilter.toUpperCase() }),
    ...(typeFilter === "" ? {} : { type: typeFilter }),
    ...(statusFilter === "" ? {} : { status: statusFilter }),
    ...(correlationFilter === "" ? {} : { correlationId: correlationFilter }),
    ...(fromFilter === "" ? {} : { from: fromFilter }),
    ...(toFilter === "" ? {} : { to: toFilter }),
  });
  const summary = useActivitySummary({
    ...(accountFilter === "" ? {} : { brokerAccountId: accountFilter }),
    ...(botFilter === "" ? {} : { botId: botFilter }),
    ...(fromFilter === "" ? {} : { from: fromFilter }),
    ...(symbolFilter === "" ? {} : { symbol: symbolFilter.toUpperCase() }),
    ...(toFilter === "" ? {} : { to: toFilter }),
  });
  const botById = new Map((bots.data ?? []).map((bot) => [bot.id, bot]));
  const accountById = new Map((accounts.data ?? []).map((account) => [account.id, account]));
  const visible = [...(activities.data?.items ?? [])].sort((left, right) =>
    right.occurredAt.localeCompare(left.occurredAt),
  );
  const pagination = usePagination(visible);
  const correlationSequence = new Map<string, readonly string[]>();
  for (const activity of visible) {
    const chain = visible
      .filter((candidate) => candidate.correlationId === activity.correlationId)
      .sort((left, right) => left.occurredAt.localeCompare(right.occurredAt))
      .map((candidate) => candidate.id);
    correlationSequence.set(activity.correlationId, chain);
  }
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "activity") next.delete("activity");
    if (key !== "activity" && key !== "cursor") {
      next.delete("cursor");
      next.delete("page");
    }
    setSearch(next, { replace: true });
  };

  return (
    <section className="page-stack page-stack--compact" aria-label="History">
      <div className="page-toolbar">
        <h1>History</h1>
        <RefreshButton queries={[activities, summary, bots, accounts]} />
      </div>
      <div className="content-card activity-summary" aria-label="Live trading summary">
        <div>
          <span>Total trades</span>
          <strong>{summary.data?.totalTrades ?? "—"}</strong>
          {summary.data === undefined ? null : (
            <small>
              {summary.data.winningTrades} wins · {summary.data.losingTrades} losses
            </small>
          )}
        </div>
        <div>
          <span>Win rate</span>
          <strong>
            {summary.data?.winRate === undefined ? "—" : `${summary.data.winRate.toFixed(2)}%`}
          </strong>
        </div>
        <div>
          <span>Realized P/L</span>
          <strong
            className={
              summary.data === undefined
                ? undefined
                : Number(summary.data.realizedPnlUsd) >= 0
                  ? "activity-summary__positive"
                  : "activity-summary__negative"
            }
          >
            {summary.data === undefined ? "—" : formatUsd(summary.data.realizedPnlUsd)}
          </strong>
          <small>Live trades only</small>
        </div>
        <div>
          <span>Range</span>
          <strong>
            {fromFilter === "" && toFilter === ""
              ? "All time"
              : `${fromFilter === "" ? "Start" : fromFilter} → ${toFilter === "" ? "Now" : toFilter}`}
          </strong>
        </div>
      </div>
      {summary.error !== null ? (
        <p className="activity-summary__error">Live trading summary unavailable.</p>
      ) : null}
      <div className="content-card activity-filters" aria-label="Activity filters">
        <div className="field-stack">
          <label htmlFor="activity-account">Account</label>
          <select
            id="activity-account"
            value={accountFilter}
            onChange={(event) => setFilter("account", event.currentTarget.value)}
          >
            <option value="">All accounts</option>
            {(accounts.data ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.displayName}
              </option>
            ))}
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="activity-bot">Bot</label>
          <select
            id="activity-bot"
            value={botFilter}
            onChange={(event) => setFilter("bot", event.currentTarget.value)}
          >
            <option value="">All Bots</option>
            {(bots.data ?? []).map((bot) => (
              <option key={bot.id} value={bot.id}>
                {bot.name} · {bot.symbol}
              </option>
            ))}
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="activity-symbol">Symbol</label>
          <input
            id="activity-symbol"
            value={symbolFilter}
            onChange={(event) => setFilter("symbol", event.currentTarget.value)}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="activity-run">Bot Run ID</label>
          <input
            id="activity-run"
            value={runFilter}
            onChange={(event) => setFilter("run", event.currentTarget.value)}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="activity-type">Event type</label>
          <select
            id="activity-type"
            value={typeFilter}
            onChange={(event) => setFilter("type", event.currentTarget.value)}
          >
            <option value="">All types</option>
            {activityTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="activity-status">Order status</label>
          <input
            id="activity-status"
            value={statusFilter}
            onChange={(event) => setFilter("status", event.currentTarget.value)}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="activity-from">From</label>
          <input
            id="activity-from"
            type="date"
            value={fromFilter}
            onChange={(event) => setFilter("from", event.currentTarget.value)}
          />
        </div>
        <div className="field-stack">
          <label htmlFor="activity-to">To</label>
          <input
            id="activity-to"
            type="date"
            value={toFilter}
            onChange={(event) => setFilter("to", event.currentTarget.value)}
          />
        </div>
      </div>
      {activities.isLoading ? (
        <ResourceState
          kind="loading"
          title="Loading activity history"
          description="Reading immutable events from TradingEngine."
        />
      ) : null}
      {activities.error !== null ? (
        <ResourceState
          kind="error"
          title="Activity history unavailable"
          description="TradingEngine could not return the audit trail. Existing records remain unchanged."
        />
      ) : null}
      {bots.error !== null || accounts.error !== null ? (
        <ResourceState
          kind="degraded"
          title="Some labels unavailable"
          description="The event history is intact, but a Bot or account name lookup failed. Stable IDs are shown instead."
        />
      ) : null}
      {!activities.isLoading && activities.error === null && visible.length === 0 ? (
        <ResourceState
          kind="empty"
          title="No matching activities"
          description="No immutable events match the current filters."
        />
      ) : null}
      <ol className="activity-timeline">
        {pagination.items.map((activity) => {
          const bot = activity.botId === undefined ? undefined : botById.get(activity.botId);
          const account =
            activity.brokerAccountId === undefined
              ? undefined
              : accountById.get(activity.brokerAccountId);
          const chain = correlationSequence.get(activity.correlationId) ?? [];
          const sequence = chain.indexOf(activity.id) + 1;
          const expanded = activityFilter === activity.id;
          return (
            <li key={activity.id} className={`activity-event activity-event--${activity.type}`}>
              <div className="activity-event__rail">
                <span aria-hidden="true" />
              </div>
              <article>
                <header>
                  <div>
                    <span className="status-pill">{activity.type}</span>
                    {activity.status === undefined ? null : (
                      <span className={`status-pill status-pill--${activity.status}`}>
                        {activity.status}
                      </span>
                    )}
                  </div>
                  <time dateTime={activity.occurredAt}>
                    {activity.occurredAt === ""
                      ? "Time unavailable"
                      : new Date(activity.occurredAt).toLocaleString()}
                  </time>
                </header>
                <h2>{activity.summary}</h2>
                <p>
                  {bot?.symbol ?? activity.symbol ?? "No symbol"} ·{" "}
                  {account?.displayName ?? activity.brokerAccountId ?? "No broker account"}
                  {chain.length > 1
                    ? ` · event ${sequence} of ${chain.length} in correlation chain`
                    : ""}
                </p>
                <div className="activity-links">
                  {activity.botId === undefined ? null : (
                    <Link to={`/bots/${activity.botId}`}>Bot {bot?.name ?? activity.botId}</Link>
                  )}
                  {activity.botRunId === undefined ? null : (
                    <button type="button" onClick={() => setFilter("run", activity.botRunId ?? "")}>
                      Run {activity.botRunId}
                    </button>
                  )}
                  {activity.orderId === undefined ? null : (
                    <button
                      type="button"
                      onClick={() => setFilter("order", activity.orderId ?? "")}
                    >
                      Order {activity.orderId}
                    </button>
                  )}
                  {activity.fillId === undefined ? null : (
                    <button type="button" onClick={() => setFilter("fill", activity.fillId ?? "")}>
                      Fill {activity.fillId}
                    </button>
                  )}
                  {activity.brokerAccountId === undefined ||
                  (bot?.symbol ?? activity.symbol) === undefined ? null : (
                    <Link
                      to={`/positions?account=${encodeURIComponent(activity.brokerAccountId)}&symbol=${encodeURIComponent(bot?.symbol ?? activity.symbol ?? "")}`}
                    >
                      Position
                    </Link>
                  )}
                  {activity.correlationId === "" ? null : (
                    <button
                      type="button"
                      onClick={() => setFilter("correlation", activity.correlationId)}
                    >
                      Correlation {activity.correlationId}
                    </button>
                  )}
                </div>
                <Button
                  onClick={() => setFilter("activity", expanded ? "" : activity.id)}
                  tone="quiet"
                >
                  {expanded ? "Hide diagnostic detail" : "View diagnostic detail"}
                </Button>
                {expanded ? (
                  <>
                    <dl className="detail-pairs">
                      {Object.entries(activity.details).map(([key, value]) => (
                        <div key={key}>
                          <dt>{key}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="activity-event__immutable-notice">
                      Historical records cannot be edited or deleted here.
                    </p>
                  </>
                ) : null}
              </article>
            </li>
          );
        })}
      </ol>
      <Pagination {...pagination} disabled={activities.isFetching || activities.error !== null} />
    </section>
  );
}
